import { useCallback, useMemo, useRef, useState, type RefObject, type SyntheticEvent } from "react";
import { isVideoLoadEvent, isVideoReady, nextVideoLoading, type VideoLoadUntil } from "./videoLoading";

/**
 * `<video>` 첫 프레임 전 로딩 표시 (BOOTH-93). 판정은 순수 `videoLoading.ts`.
 *
 * ```tsx
 * const { loading, bind } = useVideoLoading();
 * <video key={src} {...bind} src={src} autoPlay muted … />
 * {loading ? <span className="shimmer" aria-hidden /> : null}
 * ```
 * - `bind.ref` 가 붙는 시점에 이미 `readyState` 가 충분하면(캐시된 미디어·StrictMode 재마운트) 즉시 끈다 — 표시가 붙은 채 남지 않게.
 * - src 가 바뀌면 `emptied`/`loadstart` 로 다시 켜지지만, 호출부는 `key={src}` 로 리마운트하는 쪽을 권장한다.
 * - 훅은 `<video>` 가 조건부여도 컴포넌트 최상위에서 무조건 호출한다.
 * - 콘솔 `apps/booth-console/src/hooks/useVideoLoading.ts` 와 같은 코드.
 */
export function useVideoLoading(opts: { until?: VideoLoadUntil } = {}) {
  const until: VideoLoadUntil = opts.until ?? "frame";
  const [loading, setLoading] = useState(true);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const onEvent = useCallback(
    (e: SyntheticEvent<HTMLVideoElement>) => {
      const type = e.type;
      if (!isVideoLoadEvent(type)) return;
      setLoading((prev) => nextVideoLoading(prev, type, until));
    },
    [until]
  );

  const ref = useCallback(
    (el: HTMLVideoElement | null) => {
      videoRef.current = el;
      if (el && isVideoReady(el.readyState, until)) setLoading(false);
    },
    [until]
  );

  const bind = useMemo(
    () => ({
      ref,
      onLoadStart: onEvent,
      onEmptied: onEvent,
      onLoadedMetadata: onEvent,
      onLoadedData: onEvent,
      onCanPlay: onEvent,
      onPlaying: onEvent,
      onError: onEvent,
    }),
    [ref, onEvent]
  );

  return { loading, bind, videoRef: videoRef as RefObject<HTMLVideoElement | null> };
}
